import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../../components/New/Button";
import { CircleCheckbox } from "../../../../../../components/New/CircleCheckbox";
import {
  ModalSubTitle,
  ModalText,
} from "../../../../../../components/New/Modal/styles";
import { IncreaseInput } from "../../../../../../components/New/IncreaseInput";
import { wrapperRequests } from "../../../../../../services/api";
import { routesURL } from "../../../../../../services/routesUrl";
import { useContextSelector } from "use-context-selector";
import { OrdersContext } from "../../../../../../contexts/OrdersContext";
import { Checkbox } from "../../../../../../components/New/Checkbox";
import { SelectInput } from "../../../../../../components/SelectInput";
import * as Dialog from "@radix-ui/react-dialog";
import {
  OrderRefundItem,
  RefundOrderInitialValue,
  RefundOrderSale,
  RefundSaleItemsReqBody,
  OrderDetailSale,
} from "../../../../../../@types/Orders";
import { toastify } from "../../../../../../shared/Toastify";
import { newColors } from "../../../../../../styles/themes/default/New/foundations/colors";
import ReactLoading from "react-loading";
import { SearchableSelectInput } from "../../../../../../components/New/SearchableSelectInput";

import * as S from "./styles";
import {
  CustomTable,
  CustomTableRow,
} from "../../../../../../components/New/Table/CustomTable";
import { StoreToRefund } from "../../../../../../@types/Stores";
import { useTheme } from "styled-components";
import { useMediaQuery } from "usehooks-ts";
import { useTranslateConstants } from "../../hooks/useTranslateConstants";
import { Language } from "../../../../../../enums/Language";
import { WarningBanner } from "../../../../../../components/Banner";
import { useAuth } from "../../../../../../hooks/auth";
import { AxiosError } from "axios";

interface RefundModalProps {
  sourceMarketplace: "Incycle Shopify" | "RnR Shopify" | "Incycle Ebay" | "SBC";
  sales: OrderDetailSale[];
  setCloseDrawer?: () => void;
  onSuccess?: () => void;
}

export function RefundModal({
  sourceMarketplace,
  sales,
  setCloseDrawer,
  onSuccess,
}: RefundModalProps) {
  const { t, i18n } = useTranslation();

  const {
    ebayRefundReason,
    refundCondition,
    refundPercentage,
    refundReason,
    internalReasonRefund,
  } = useTranslateConstants();

  const [initialEntireRefundItems, setInitialEntireRefundItems] = useState<
    RefundOrderSale[]
  >([]);
  const [entireRefundItems, setEntireRefundItems] = useState<RefundOrderSale[]>(
    [],
  );
  const [errorMessage, setErrorMessage] = useState({
    message: "",
    saleLineID: [""],
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialRefundItems, setInitialRefundItems] = useState<
    RefundOrderInitialValue[]
  >([]);

  const [refundItems, setRefundItems] = useState<RefundSaleItemsReqBody[]>([]);
  const [refundType, setRefundType] = useState<"entire" | "individual">(
    "entire",
  );
  const [allStores, setAllStores] = useState<StoreToRefund[]>([]);

  const { orderDetails } = useContextSelector(OrdersContext, (context) => {
    return context;
  });

  const refundButtonDisabled =
    (refundType === "individual" && !refundItems[0]) ||
    loading ||
    orderDetails.permissions?.refundEnabled === false;

  const { responsiveDesign } = useTheme();
  const { canRefundAndCancelByCountry, isUnitedStates } = useAuth();

  const isResponsive = useMediaQuery(`(max-width: ${responsiveDesign.sm})`);

  const isEnglishLanguage = i18n.language === Language.ENGLISH;

  const reorganizedProductsToTable = initialRefundItems.flatMap(
    ({ saleID, items }) =>
      items.map((item) => ({
        ...item,
        saleID,
      })),
  );

  const isEbay = sourceMarketplace === "Incycle Ebay";
  const ebayProductsAlreadyHasQuantity = reorganizedProductsToTable.some(
    (item) => item.refundID,
  );

  const individualItemsQuantityElement = (product: OrderRefundItem) => {
    if (ebayProductsAlreadyHasQuantity) {
      return (
        <p>{`${product.quantityToRefund}/${product.quantityPurchased}`}</p>
      );
    } else {
      const productSelected = refundItems.find(
        (item) =>
          item.productID === product.productID &&
          item.saleID === product.saleID,
      );

      return (
        <IncreaseInput
          disabled={!productSelected || loading}
          handleIncreaseValue={() =>
            handleChangeQuantity(
              product.productID,
              product.saleID,
              "increase",
              product.quantityToRefund,
              productSelected?.quantity || 1,
            )
          }
          handleDecreaseValue={() =>
            handleChangeQuantity(
              product.productID,
              product.saleID,
              "decrease",
              product.quantityToRefund,
              productSelected?.quantity || 1,
            )
          }
          maxValue={product.quantityToRefund}
          value={productSelected?.quantity || 0}
        />
      );
    }
  };

  const getRefundProducts = async () => {
    const response = await wrapperRequests(
      routesURL.report.orders.getRefundSalesItems(orderDetails.id),
      "GET",
    );

    setInitialRefundItems(response.data);
    setInitialLoading(false);

    const transformArray = response.data.map(
      (sale: RefundOrderInitialValue) => ({
        saleID: sale.saleID,
        saleLines: sale.items.map((item) => ({
          internalReason: "",
          saleLineID: item.saleLineID,
          quantity: item.quantityToRefund,
          reason: isEbay ? "BUYER_CANCEL" : "customer",
          condition: "New",
          refundPercentage: "100%",
          shopID: "",
          tenantName: "",
          saleID: sale.saleID,
          productID: item.productID,
          ...(isEbay && item?.refundID ? { refundID: item.refundID } : {}),
        })),
      }),
    );

    setInitialEntireRefundItems(transformArray);
    setEntireRefundItems(transformArray);
  };

  const findStoreFromSale = (
    stores: StoreToRefund[],
    sale: OrderDetailSale,
  ): StoreToRefund | undefined => {
    return stores.find(
      (store) =>
        store.tenantName?.toLowerCase() === sale.tenantName?.toLowerCase() &&
        store.code === sale.shopID,
    );
  };

  const selectInitialStore = (stores: StoreToRefund[]) => {
    const firstSale = sales?.[0];

    if (isUnitedStates) {
      const pomonaStore = stores.find(
        (store) =>
          store.name?.toLowerCase().includes("pomona") &&
          store.tenantName?.toLowerCase() === "incycle",
      );

      if (pomonaStore) {
        return {
          id: pomonaStore.id,
          tenantName: pomonaStore.tenantName,
          name: pomonaStore.name,
          code: pomonaStore.code,
        };
      }
    }

    if (firstSale) {
      const storeFromSale = findStoreFromSale(stores, firstSale);
      if (storeFromSale) {
        return {
          id: storeFromSale.id,
          tenantName: storeFromSale.tenantName,
          name: storeFromSale.name,
          code: storeFromSale.code,
        };
      }
    }

    return stores[0];
  };

  const getAllStoresOptions = async () => {
    try {
      const response = await wrapperRequests(
        routesURL.settings.stores.getCustomAllStores([
          "name",
          "displayName",
          "code",
          "tenantName",
        ]),
        "GET",
      );

      const stores = response.data.content;
      setAllStores(stores);

      const initialStore = selectInitialStore(stores);

      setEntireRefundItems((prevItems) =>
        prevItems.map((product) => {
          const updatedSaleLines = product.saleLines.map((saleLine) => ({
            ...saleLine,
            shopID: initialStore.code,
            tenantName: initialStore.tenantName,
          }));

          return {
            ...product,
            saleLines: updatedSaleLines,
          };
        }),
      );

      setInitialEntireRefundItems((prevItems) =>
        prevItems.map((product) => {
          const updatedSaleLines = product.saleLines.map((saleLine) => ({
            ...saleLine,
            shopID: initialStore.code,
            tenantName: initialStore.tenantName,
          }));

          return {
            ...product,
            saleLines: updatedSaleLines,
          };
        }),
      );
    } catch (error) {
      throw Error(String(error));
    }
  };

  const addOrRemoveItem = (product: OrderRefundItem) => {
    setRefundItems((prevProducts) => {
      const existingIndex = prevProducts.findIndex(
        (p) => p.productID === product.productID && p.saleID === product.saleID,
      );

      if (existingIndex === -1) {
        const initialStore = selectInitialStore(allStores);

        return [
          ...prevProducts,
          {
            internalReason: "",
            condition: "New",
            ...(ebayProductsAlreadyHasQuantity ? {} : { quantity: 1 }),
            reason: isEbay ? "BUYER_CANCEL" : "customer",
            refundPercentage: "100%",
            ...(isEbay ? { refundID: product.refundID } : {}),
            saleLineID: product.saleLineID,
            saleID: product.saleID,
            shopID: initialStore.code,
            tenantName: initialStore.tenantName,
            productID: product.productID,
            availableQuantity: product.quantityToRefund,
          },
        ];
      } else {
        return prevProducts.filter((_, index) => index !== existingIndex);
      }
    });
  };

  const handleChangeQuantity = (
    productID: string,
    saleID: string,
    type: "increase" | "decrease",
    maxValue: number,
    value: number,
  ) => {
    setRefundItems((prevProducts) =>
      prevProducts.map((product) => {
        if (product.productID === productID && product.saleID === saleID) {
          const newQuantity =
            type === "increase"
              ? value < maxValue
                ? value + 1
                : value
              : value > 1
                ? value - 1
                : value;
          return { ...product, quantity: newQuantity };
        }
        return product;
      }),
    );
  };

  const handleChangeSelectInputs = (
    productID: string,
    saleID: string,
    type:
      | "reason"
      | "refundPercentage"
      | "condition"
      | "stockLocation"
      | "internalReason",
    newValue: string,
    secondValue?: string,
  ) => {
    setRefundItems((prevProducts) =>
      prevProducts.map((product) => {
        if (product.productID === productID && product.saleID === saleID) {
          if (!secondValue) {
            return {
              ...product,
              [type]: newValue,
            };
          }

          return {
            ...product,
            shopID: newValue,
            tenantName: secondValue,
          };
        }

        return product;
      }),
    );
  };

  const handleChangeSelectInputsEntireRefund = (
    type:
      | "reason"
      | "condition"
      | "refundPercentage"
      | "stockLocation"
      | "internalReason",
    newValue: string,
    secondValue?: string,
  ) => {
    setEntireRefundItems((prevProducts) =>
      prevProducts.map((product) => {
        const updatedSaleLines = product.saleLines.map((saleLine) => {
          if (!secondValue) {
            return {
              ...saleLine,
              [type]: newValue,
            };
          }

          return {
            ...saleLine,
            shopID: newValue,
            tenantName: secondValue,
          };
        });

        return {
          ...product,
          saleLines: updatedSaleLines,
        };
      }),
    );
  };

  const itemsWithoutInternalReason = () => {
    if (refundType === "individual") {
      return [
        ...refundItems
          .filter((item) => item.internalReason.trim() === "")
          .map((item) => `${item.productID}-${item.saleID}`),
      ];
    }

    return [
      ...entireRefundItems.flatMap((sale) =>
        sale.saleLines
          .filter((item) => item.internalReason.trim() === "")
          .map((item) => `${item.productID}-${sale.saleID}`),
      ),
    ];
  };

  const lineHasError = (product: OrderRefundItem) => {
    return errorMessage.saleLineID.includes(
      `${product.productID}-${product.saleID}`,
    );
  };

  const handleSubmitRefund = async () => {
    const saleLineIDArray = itemsWithoutInternalReason();

    if (saleLineIDArray.length > 0) {
      setErrorMessage({
        message: t("returnsInternalReasonRequired"),
        saleLineID: [...saleLineIDArray],
      });
      return;
    }

    setLoading(true);
    const grouped: { [key: string]: RefundSaleItemsReqBody[] } = {};

    refundItems.forEach((product) => {
      const currentSaleID = product.saleID ?? "";
      if (!currentSaleID) return;
      if (!grouped[currentSaleID]) {
        grouped[currentSaleID] = [];
      }

      grouped[currentSaleID].push({
        internalReason: product.internalReason,
        saleLineID: product.saleLineID,
        quantity: product.quantity,
        reason: product.reason,
        condition: product.condition,
        tenantName: product.tenantName,
        shopID: product.shopID,
        refundPercentage: product.refundPercentage,
        productID: product.productID,
        availableQuantity: product.availableQuantity,
      });
    });

    const reorganizedArray = Object.keys(grouped).map((saleID) => ({
      saleID,
      saleLines: grouped[saleID],
    }));

    let allItemsWithMaxQuantity;

    if (refundItems.length !== reorganizedProductsToTable.length) {
      allItemsWithMaxQuantity = false;
    } else {
      allItemsWithMaxQuantity = reorganizedArray.every((sale) =>
        sale.saleLines.every(
          (item) =>
            item.availableQuantity === item.quantity &&
            item.quantity &&
            item.quantity > 0,
        ),
      );
    }

    const individualRefundBody = {
      refundEntireOrder: allItemsWithMaxQuantity,
      sales: reorganizedArray,
    };

    const entireRefundBody = {
      refundEntireOrder: true,
      sales: entireRefundItems,
    };

    try {
      await wrapperRequests(routesURL.report.orders.refund, "POST", {
        data: refundType === "entire" ? entireRefundBody : individualRefundBody,
      });
      toastify("success", t("refundCompleted"), "top-center");

      if (onSuccess) {
        onSuccess();
      }

      if (setCloseDrawer) {
        setCloseDrawer();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toastify(
          "error",
          error.response?.data?.message || t("refundError"),
          "top-center",
        );
      } else {
        toastify("error", t("refundError"), "top-center");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEbaySubmitRefund = async () => {
    const saleLineIDArray = itemsWithoutInternalReason();

    if (saleLineIDArray.length > 0) {
      setErrorMessage({
        message: t("returnsInternalReasonRequired"),
        saleLineID: [...saleLineIDArray],
      });
      return;
    }

    setLoading(true);

    const transformEntireRefundItemsArray = entireRefundItems.flatMap(
      (item) => item.saleLines,
    );

    const individualRefundBody = {
      refunds: refundItems,
    };

    const entireRefundBody = {
      refunds: transformEntireRefundItemsArray,
    };

    try {
      await wrapperRequests(routesURL.report.orders.refund, "POST", {
        data: refundType === "entire" ? entireRefundBody : individualRefundBody,
      });
      toastify("success", t("refundCompleted"), "top-center");

      if (onSuccess) {
        onSuccess();
      }

      if (setCloseDrawer) {
        setCloseDrawer();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toastify(
          "error",
          error.response?.data?.message || t("refundError"),
          "top-center",
        );
      } else {
        toastify("error", t("refundError"), "top-center");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await getRefundProducts();
      await getAllStoresOptions();
    };

    loadData();
  }, []);

  return (
    <S.ModalContainer isLoading={initialLoading}>
      {initialLoading && (
        <ReactLoading
          height={25}
          width={25}
          type={"spin"}
          color={newColors["gray-600"]}
        />
      )}

      {!initialLoading && allStores[0] && (
        <>
          <ModalSubTitle>{t("whatWouldYouLikeToRefund")}</ModalSubTitle>
          {!orderDetails?.permissions?.marketplaceRefundEnabled && (
            <WarningBanner>
              <p>{t("refundNotExecutedInMarketplace")}</p>
            </WarningBanner>
          )}

          <S.OptionsAlign>
            <CircleCheckbox
              label={t("entireOrder")}
              onClick={() => {
                setRefundType("entire");
                setRefundItems([]);
                setEntireRefundItems(initialEntireRefundItems);
                setErrorMessage({
                  message: "",
                  saleLineID: [""],
                });
              }}
              checked={refundType === "entire"}
              disabled={loading}
            />

            <CircleCheckbox
              label={t("individualItems")}
              onClick={() => {
                setRefundType("individual");
                setErrorMessage({
                  message: "",
                  saleLineID: [""],
                });
              }}
              checked={refundType === "individual"}
              disabled={loading}
            />
          </S.OptionsAlign>

          {refundType === "entire" && (
            <S.ModalContent>
              <ModalSubTitle>{t("selectReasonForRefund")}</ModalSubTitle>

              <SelectInput
                options={isEbay ? ebayRefundReason : refundReason}
                variant="resized"
                onChange={(e) =>
                  handleChangeSelectInputsEntireRefund("reason", e.target.value)
                }
                disabled={loading}
              />

              {canRefundAndCancelByCountry && (
                <>
                  <ModalSubTitle>{t("selectStoreForReturn")}</ModalSubTitle>

                  <SearchableSelectInput
                    initialValue={selectInitialStore(allStores)}
                    options={allStores}
                    onOptionSelect={(e) => {
                      handleChangeSelectInputsEntireRefund(
                        "stockLocation",
                        e.code,
                        e.tenantName,
                      );
                    }}
                  />
                </>
              )}

              <ModalSubTitle>{t("conditionOfReturnedItem")}</ModalSubTitle>

              <SelectInput
                options={refundCondition}
                variant="resized"
                onChange={(e) =>
                  handleChangeSelectInputsEntireRefund(
                    "condition",
                    e.target.value,
                  )
                }
                disabled={loading}
              />

              <ModalSubTitle>{t("returnsInternalReasonCode")}</ModalSubTitle>

              <SelectInput
                placeholder={t("selectAnOption")}
                options={internalReasonRefund}
                onChange={(e) => {
                  handleChangeSelectInputsEntireRefund(
                    "internalReason",
                    e.target.value,
                  );
                  setErrorMessage({
                    message: "",
                    saleLineID: [""],
                  });
                }}
                disabled={loading}
                variant="resized"
                error={errorMessage.message}
              />

              <ModalSubTitle>{t("refundPercentage")}</ModalSubTitle>

              <SelectInput
                options={refundPercentage}
                variant="resized"
                onChange={(e) =>
                  handleChangeSelectInputsEntireRefund(
                    "refundPercentage",
                    e.target.value,
                  )
                }
                disabled={loading}
              />
            </S.ModalContent>
          )}

          {refundType === "individual" && (
            <S.ModalContent>
              <ModalSubTitle>{t("items")}</ModalSubTitle>
              <ModalText style={{ margin: 0 }}>
                {t("selectItemsToRefund")}
              </ModalText>

              {!!initialRefundItems.length && (
                <CustomTable
                  headers={[
                    t("product"),
                    t("qty"),
                    t("reason"),
                    t("rirc"),
                    t("refundPercentageShort"),
                    ...(canRefundAndCancelByCountry ? [t("backToStore")] : []),
                    t("condition"),
                  ]}
                  style={{
                    width: isResponsive ? "65rem" : "100%",
                    overflow: "auto",
                    maxHeight: 250,
                  }}
                >
                  {reorganizedProductsToTable.map(
                    (product: OrderRefundItem) => {
                      const productSelected = refundItems.find(
                        (item) =>
                          item.productID === product.productID &&
                          item.saleID === product.saleID,
                      );

                      return (
                        <CustomTableRow
                          key={`${product.productID}-${product.saleID}`}
                          style={{
                            height: lineHasError(product) ? "5rem" : "2rem",
                          }}
                        >
                          <td style={{ width: "16rem" }}>
                            <S.CheckBoxRow title={product.name}>
                              <div>
                                <Checkbox
                                  checked={!!productSelected}
                                  disabled={
                                    loading || product.quantityToRefund === 0
                                  }
                                  onClick={() => {
                                    addOrRemoveItem(product);
                                  }}
                                />
                              </div>
                              {product.name}
                            </S.CheckBoxRow>
                          </td>
                          <td style={{ width: "5rem" }}>
                            {individualItemsQuantityElement(product)}
                          </td>
                          <td style={{ width: "7.5rem" }}>
                            <SelectInput
                              disabled={!productSelected || loading}
                              options={isEbay ? ebayRefundReason : refundReason}
                              variant="resized"
                              onChange={(e) =>
                                handleChangeSelectInputs(
                                  product.productID,
                                  product.saleID,
                                  "reason",
                                  e.target.value,
                                )
                              }
                              value={
                                productSelected?.reason ||
                                (isEbay ? "BUYER_CANCEL" : "customer")
                              }
                            />
                          </td>
                          <td style={{ width: "10rem" }}>
                            <SelectInput
                              disabled={!productSelected || loading}
                              options={internalReasonRefund}
                              onChange={(e) => {
                                handleChangeSelectInputs(
                                  product.productID,
                                  product.saleID,
                                  "internalReason",
                                  e.target.value,
                                );
                                setErrorMessage((currentValue) => {
                                  return {
                                    ...currentValue,
                                    saleLineID: currentValue.saleLineID.filter(
                                      (key) =>
                                        key !==
                                        `${product.productID}-${product.saleID}`,
                                    ),
                                  };
                                });
                              }}
                              variant="resized"
                              error={
                                lineHasError(product)
                                  ? errorMessage.message
                                  : ""
                              }
                              isSmallErrorMessage
                            />
                          </td>
                          <td style={{ width: "5.7rem" }}>
                            <SelectInput
                              disabled={!productSelected || loading}
                              options={refundPercentage}
                              variant="resized"
                              onChange={(e) =>
                                handleChangeSelectInputs(
                                  product.productID,
                                  product.saleID,
                                  "refundPercentage",
                                  e.target.value,
                                )
                              }
                              value={
                                productSelected?.refundPercentage || "100%"
                              }
                            />
                          </td>
                          {canRefundAndCancelByCountry && (
                            <td style={{ width: "11rem" }}>
                              <SearchableSelectInput
                                initialValue={selectInitialStore(allStores)}
                                options={allStores}
                                onOptionSelect={(e) => {
                                  handleChangeSelectInputs(
                                    product.productID,
                                    product.saleID,
                                    "stockLocation",
                                    e.code,
                                    e.tenantName,
                                  );
                                }}
                                width={"5rem"}
                                disabled={!productSelected || loading}
                              />
                            </td>
                          )}
                          <td style={{ width: "8rem" }}>
                            <SelectInput
                              disabled={!productSelected || loading}
                              options={refundCondition}
                              variant="resized"
                              onChange={(e) =>
                                handleChangeSelectInputs(
                                  product.productID,
                                  product.saleID,
                                  "condition",
                                  e.target.value,
                                )
                              }
                              value={productSelected?.condition || "New"}
                            />
                          </td>
                        </CustomTableRow>
                      );
                    },
                  )}
                </CustomTable>
              )}
            </S.ModalContent>
          )}

          {isResponsive && setCloseDrawer && (
            <S.ResponsiveButtonContainer>
              <Button
                disabled={refundButtonDisabled}
                isLoading={loading}
                onClick={isEbay ? handleEbaySubmitRefund : handleSubmitRefund}
                title={t("submitRefund")}
              />
              <Button
                variant="ghost"
                title={t("cancel")}
                isLoading={loading}
                onClick={() => setCloseDrawer()}
              />
            </S.ResponsiveButtonContainer>
          )}

          {!isResponsive && (
            <S.ButtonAlign>
              <Dialog.Close asChild>
                <Button
                  variant="ghost"
                  title={t("cancel")}
                  width="8.2rem"
                  disabled={loading}
                />
              </Dialog.Close>
              <Button
                title={t("submitRefund")}
                width={isEnglishLanguage ? "8.2rem" : "auto"}
                onClick={isEbay ? handleEbaySubmitRefund : handleSubmitRefund}
                disabled={refundButtonDisabled}
                isLoading={loading}
              />
            </S.ButtonAlign>
          )}
        </>
      )}
    </S.ModalContainer>
  );
}
